import { useCallback, useMemo, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  SearchForm,
  SearchFormProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableRowWithLink,
  IconButton,
} from '@occc/ui-design-system';

import { type ChipItem } from '.';

type ItemsTableProps = {
  items: Array<ChipItem & { id?: string }>;
  title: string;
  itemName: string;
  onClose?: () => void;
  sx?: SxProps;
  tableSx?: SxProps;
};

export const ItemsTable = ({ items, title, itemName, onClose, sx, tableSx }: ItemsTableProps) => {
  const [search, setSearch] = useState('');

  const hasIds = useMemo(() => items.some((items) => items.id), [items]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (!search?.trim()) return items;

    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const isLabelInclude = item.label.toLowerCase().includes(q);
      if (!item.id) {
        return isLabelInclude;
      }
      return isLabelInclude || item.id.toLowerCase().includes(q);
    });
  }, [items, search]);

  const handleSearchSubmit = useCallback<SearchFormProps['onSubmit']>(({ search }) => {
    setSearch(search ?? '');
  }, []);

  return (
    <TableContainer elevation={0} sx={sx} square>
      <TableHeader
        title={title}
        actions={
          <Box display='flex' alignItems='center' gap={1}>
            <SearchForm onSubmit={handleSearchSubmit} initialValues={{ search }} />
            {onClose && (
              <IconButton onClick={onClose} size='medium' aria-label='Close dialog'>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            )}
          </Box>
        }
      />
      <Box sx={tableSx}>
        <Table>
          {hasIds && (
            <TableHead>
              <TableRow hover={false}>
                <TableCell>{itemName} ID</TableCell>
                <TableCell>{itemName} Name</TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow hover={false}>
                <TableCell colSpan={hasIds ? 2 : undefined} align='center'>
                  {items.length === 0 ? `No ${itemName}s` : `No matching ${itemName}s`}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item, index) => {
                const content = (
                  <>
                    {hasIds && <TableCell>{item.id}</TableCell>}
                    <TableCell>{item.label}</TableCell>
                  </>
                );
                return item.href ? (
                  <TableRowWithLink key={item.id || index} to={item.href}>
                    {content}
                  </TableRowWithLink>
                ) : (
                  <TableRow key={item.id || index} onClick={item.onClick}>
                    {content}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </TableContainer>
  );
};
